import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Form, Button, Table, Badge, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import axios from 'axios';
import Cookies from 'universal-cookie';
import Swal from 'sweetalert2';
import { getAllUsers } from '../reduxLogic/reducers/auth/getAllUsersSlice';
import { updateUser } from '../reduxLogic/reducers/users/makeUserSlice';
import * as S from './AdvancesStyles';

const cookies = new Cookies();

const ROLE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'bee_mitra', label: 'Bee Mitra' },
  { value: 'field_view_app_admin', label: 'Field View App Admin' },
  { value: 'trainee_beekeeper', label: 'Trainee Beekeeper' },
  { value: 'beekeeper', label: 'Beekeeper' },
  { value: 'community_leader', label: 'Community Leader' },
  { value: 'farmer', label: 'Farmer' }
];

const getAuthHeaders = () => {
  const rawToken = cookies.get('access_token');
  const tokenStr = rawToken
    ? typeof rawToken === 'string' && (rawToken.startsWith('{') || rawToken.startsWith('"'))
      ? JSON.parse(rawToken)
      : rawToken
    : '';

  return {
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${tokenStr}`,
      'x-auth-token': tokenStr,
      source: 'humblebee_admin',
    },
  };
};

const ReportingMappingTab = ({ members = [], projects = [], isAdmin = true }) => {
  const dispatch = useDispatch();
  const { allUsers = [], loading: reduxLoading } = useSelector(state => state.allUsers || {});

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('field_view_app_admin');
  const [selectedProjectFilter, setSelectedProjectFilter] = useState('all');
  const [managerAssignments, setManagerAssignments] = useState({});
  const [initialManagerAssignments, setInitialManagerAssignments] = useState({});
  const [savingUserId, setSavingUserId] = useState(null);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [isBulkAssigningEmpIds, setIsBulkAssigningEmpIds] = useState(false);

  useEffect(() => {
    if (!allUsers || allUsers.length === 0) {
      dispatch(getAllUsers());
    }
  }, [dispatch, allUsers]);

  // Combine supervisors (members) and allUsers for manager options
  const managerOptions = useMemo(() => {
    const map = new Map();

    // 1. Add supervisors from members prop
    members.forEach(m => {
      if (m && m.id) {
        map.set(String(m.id), {
          value: String(m.id),
          label: `${m.name || 'Staff'} ${m.employee_id ? `(${m.employee_id})` : ''}`,
          name: m.name || 'Staff',
          employee_id: m.employee_id || ''
        });
      }
    });

    // 2. Add all users from Redux
    (allUsers || []).forEach(u => {
      const uId = String(u._id || u.id || u.userInfo?._id || '');
      const uName = u.name || u.userInfo?.name || u.username || 'User';
      const uEmp = u.employee_id || u.userInfo?.employee_id || u.employeeId || '';
      if (uId && !map.has(uId)) {
        map.set(uId, {
          value: uId,
          label: `${uName} ${uEmp ? `(${uEmp})` : ''}`,
          name: uName,
          employee_id: uEmp
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [members, allUsers]);

  // Initialize initial manager mapping state from user data
  useEffect(() => {
    const initialMap = {};
    (allUsers || []).forEach(u => {
      const uObj = u.userInfo || u || {};
      const uId = String(uObj._id || uObj.id || '');
      const repId = uObj.reporting_to || u.reporting_to || uObj.supervisor_id || uObj.supervisorId;
      if (uId && repId) {
        const strVal = typeof repId === 'object' ? String(repId._id || repId.id) : String(repId);
        if (strVal && strVal !== 'null' && strVal !== 'undefined') {
          initialMap[uId] = strVal;
        }
      }
    });
    setInitialManagerAssignments(initialMap);
    setManagerAssignments(initialMap);
  }, [allUsers]);

  // Compute modified mappings count
  const dirtyCount = useMemo(() => {
    let count = 0;
    Object.keys(managerAssignments).forEach(uId => {
      const current = managerAssignments[uId] || '';
      const initial = initialManagerAssignments[uId] || '';
      if (current !== initial) count++;
    });
    return count;
  }, [managerAssignments, initialManagerAssignments]);

  // Filtered staff list
  const filteredStaffList = useMemo(() => {
    let list = [...(allUsers || [])];

    // Filter by role
    if (selectedRoleFilter !== 'all') {
      list = list.filter(u => {
        const uObj = u.userInfo || u || {};
        const roles = uObj.userRoles || u.userRoles || [];
        return roles.includes(selectedRoleFilter);
      });
    }

    // Filter by project
    if (selectedProjectFilter !== 'all') {
      list = list.filter(u => {
        const uObj = u.userInfo || u || {};
        const pId = uObj.projectId || u.projectId || uObj.adminMetaInfo?.projectId || (uObj.fieldViewData && uObj.fieldViewData[0]?.project_id);
        const strPId = typeof pId === 'object' ? String(pId?._id || pId?.id) : String(pId || '');
        return strPId === selectedProjectFilter;
      });
    }

    // Filter by search term (Name / Mobile / Employee ID)
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      list = list.filter(u => {
        const uObj = u.userInfo || u || {};
        const name = (uObj.name || u.name || '').toLowerCase();
        const mobile = (uObj.mobileNumber || u.mobileNumber || uObj.mobile || '').toLowerCase();
        const empId = (uObj.employee_id || u.employee_id || uObj.employeeId || '').toLowerCase();
        return name.includes(term) || mobile.includes(term) || empId.includes(term);
      });
    }

    return list;
  }, [allUsers, selectedRoleFilter, selectedProjectFilter, searchTerm]);

  // Handle single user manager mapping save
  const handleSaveMapping = async (user) => {
    const rawUser = user?.userInfo || user || {};
    const uId = String(rawUser._id || user?._id || user?.id || '');
    const selectedManagerId = managerAssignments[uId] || null;

    if (!uId) {
      toast.error('Invalid user ID');
      return;
    }

    setSavingUserId(uId);
    try {
      const authHeaders = getAuthHeaders();
      const humblebeeUrl = process.env.REACT_APP_HUMBLEBEE_API_URL || 'https://devapi.thehumblebee.co/api/v1/humblebee';
      const appUserUrl = process.env.REACT_APP_API_APP_USER_URL || 'https://devapi.udyamita.org/user-app';

      const updatePayload = {
        ...rawUser,
        _id: uId,
        reporting_to: selectedManagerId || null,
      };

      // 1. Send update to Humblebee API /users/update
      let saved = false;
      try {
        await axios.post(
          `${humblebeeUrl}/users/update`,
          { userInfo: updatePayload },
          authHeaders
        );
        saved = true;
      } catch (hbErr) {
        console.warn('Humblebee /users/update failed, trying user-app endpoints:', hbErr?.message);
      }

      // 2. Also send to Udyamita user-app /user/update-reporting-manager and /user/update-user
      try {
        await axios.post(
          `${appUserUrl}/user/update-reporting-manager`,
          {
            userId: uId,
            reporting_to: selectedManagerId || null,
          },
          authHeaders
        );
        saved = true;
      } catch (appErr) {
        console.warn('user/update-reporting-manager notice:', appErr?.message);
      }

      try {
        await axios.post(
          `${appUserUrl}/user/update-user`,
          { userInfo: updatePayload },
          authHeaders
        );
        saved = true;
      } catch (appUserErr) {
        console.warn('user/update-user notice:', appUserErr?.message);
      }

      // Update state immediately so UI updates instantly and unsaved count drops
      setInitialManagerAssignments(prev => ({
        ...prev,
        [uId]: selectedManagerId || '',
      }));
      setManagerAssignments(prev => ({
        ...prev,
        [uId]: selectedManagerId || '',
      }));

      toast.success(`Reporting Manager updated for ${rawUser.name || 'User'}!`);
      dispatch(getAllUsers());
    } catch (err) {
      console.error('Failed to update reporting manager:', err);
      toast.error('Failed to save Reporting Manager. Please try again.');
    } finally {
      setSavingUserId(null);
    }
  };

  // Handle bulk save for all modified rows
  const handleBulkSave = async () => {
    const mappingsToSave = [];
    Object.keys(managerAssignments).forEach(uId => {
      const current = managerAssignments[uId] || '';
      const initial = initialManagerAssignments[uId] || '';
      if (current !== initial) {
        mappingsToSave.push({
          userId: uId,
          user_id: uId,
          reporting_to: current || null,
        });
      }
    });

    if (mappingsToSave.length === 0) {
      toast.info('No changes to save.');
      return;
    }

    setIsBulkSaving(true);
    try {
      const authHeaders = getAuthHeaders();
      const humblebeeUrl = process.env.REACT_APP_HUMBLEBEE_API_URL || 'https://devapi.thehumblebee.co/api/v1/humblebee';
      const appUserUrl = process.env.REACT_APP_API_APP_USER_URL || 'https://devapi.udyamita.org/user-app';

      // 1. Try bulk endpoint on user-app
      try {
        await axios.post(
          `${appUserUrl}/user/bulk-update-reporting-managers`,
          { mappings: mappingsToSave },
          authHeaders
        );
      } catch (bulkErr) {
        console.warn('Bulk endpoint notice, saving via batch loop:', bulkErr?.message);
      }

      // 2. Also save each to humblebee /users/update
      for (const item of mappingsToSave) {
        try {
          await axios.post(
            `${humblebeeUrl}/users/update`,
            {
              userInfo: {
                _id: item.userId,
                reporting_to: item.reporting_to,
              },
            },
            authHeaders
          );
        } catch (e) {
          console.warn(`Failed for ${item.userId}:`, e?.message);
        }
      }

      toast.success(`Successfully saved Reporting Managers for ${mappingsToSave.length} users!`);
      setInitialManagerAssignments(prev => ({ ...prev, ...managerAssignments }));
      dispatch(getAllUsers());
    } catch (err) {
      console.error('Bulk update reporting managers failed:', err);
      toast.error('Failed to save some changes. Please try again.');
    } finally {
      setIsBulkSaving(false);
    }
  };

  // Auto-assign sequential Employee IDs for staff missing Employee IDs
  const handleAutoAssignEmployeeIds = async () => {
    const result = await Swal.fire({
      title: 'Auto-Assign Employee IDs?',
      text: 'This will automatically generate sequential BW Employee IDs (e.g. BW0001, BW0002...) for all active staff missing an Employee ID.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0284c7',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, Generate IDs',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    setIsBulkAssigningEmpIds(true);
    try {
      const appUserUrl = process.env.REACT_APP_API_APP_USER_URL || 'https://devapi.udyamita.org/user-app';
      const response = await axios.post(
        `${appUserUrl}/user/bulk-assign-employee-ids`,
        { prefix: 'BW' },
        getAuthHeaders()
      );

      toast.success(response.data?.message || 'Employee IDs auto-assigned successfully!');
      dispatch(getAllUsers());
    } catch (err) {
      console.error('Auto-assign employee IDs error:', err);
      toast.error('Failed to auto-assign Employee IDs. Please check backend connection.');
    } finally {
      setIsBulkAssigningEmpIds(false);
    }
  };

  const mappedCount = useMemo(() => {
    return Object.values(managerAssignments).filter(Boolean).length;
  }, [managerAssignments]);

  return (
    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
      {/* Header Filters & Action Buttons */}
      <Row className="mb-3 align-items-center">
        <Col lg={3} md={6} sm={12} className="mb-2">
          <Form.Group className="mb-0">
            <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Search Employee</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search name, mobile, ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ borderRadius: '8px', fontSize: '13px' }}
            />
          </Form.Group>
        </Col>

        <Col lg={2} md={3} sm={6} className="mb-2">
          <Form.Group className="mb-0">
            <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Filter by Role</Form.Label>
            <Form.Control
              as="select"
              value={selectedRoleFilter}
              onChange={(e) => setSelectedRoleFilter(e.target.value)}
              style={{ borderRadius: '8px', fontSize: '13px' }}
            >
              {ROLE_FILTER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>

        <Col lg={3} md={3} sm={6} className="mb-2">
          <Form.Group className="mb-0">
            <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Filter by Project</Form.Label>
            <Form.Control
              as="select"
              value={selectedProjectFilter}
              onChange={(e) => setSelectedProjectFilter(e.target.value)}
              style={{ borderRadius: '8px', fontSize: '13px' }}
            >
              <option value="all">All Projects</option>
              {projects.map(p => (
                <option key={p.id || p._id} value={String(p.id || p._id)}>{p.name}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>

        {isAdmin && (
          <Col lg={4} md={12} className="mb-2 text-lg-end text-start d-flex flex-wrap align-items-center justify-content-lg-end gap-2">
            <Button
              variant="success"
              size="sm"
              disabled={dirtyCount === 0 || isBulkSaving}
              onClick={handleBulkSave}
              style={{
                borderRadius: '8px',
                fontSize: '12.5px',
                fontWeight: 600,
                padding: '7px 14px',
                boxShadow: dirtyCount > 0 ? '0 2px 8px rgba(34, 197, 94, 0.4)' : 'none'
              }}
            >
              {isBulkSaving ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span className="ms-1">Saving...</span>
                </>
              ) : (
                `Save All Changes (${dirtyCount})`
              )}
            </Button>

            <Button
              variant="outline-primary"
              size="sm"
              disabled={isBulkAssigningEmpIds}
              onClick={handleAutoAssignEmployeeIds}
              style={{
                borderRadius: '8px',
                fontSize: '12.5px',
                fontWeight: 600,
                padding: '7px 12px'
              }}
              title="Generate sequential BW0001, BW0002... for staff without Employee IDs"
            >
              {isBulkAssigningEmpIds ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span className="ms-1">Generating...</span>
                </>
              ) : (
                '⚡ Auto-Assign IDs'
              )}
            </Button>
          </Col>
        )}
      </Row>

      {/* Summary Stats Strip */}
      <div className="mb-3 d-flex flex-wrap align-items-center justify-content-between p-2 px-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '12.5px' }}>
        <div className="d-flex align-items-center gap-3">
          <span>Total Staff: <strong style={{ color: '#0f172a' }}>{allUsers.length}</strong></span>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <span>Mapped Managers: <strong style={{ color: '#0369a1' }}>{mappedCount}</strong></span>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <span>Filtered Count: <strong style={{ color: '#475569' }}>{filteredStaffList.length}</strong></span>
        </div>
        {dirtyCount > 0 && (
          <Badge bg="warning" text="dark" style={{ fontSize: '11px', padding: '5px 10px' }}>
            ⚠️ {dirtyCount} unsaved manager change{dirtyCount > 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Staff & Reporting Manager Mapping Table */}
      <div className="table-responsive" style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <Table hover align="middle" className="mb-0">
          <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
            <tr>
              <th style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Employee Name</th>
              <th style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Employee ID</th>
              <th style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Role</th>
              <th style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Assigned Project</th>
              <th style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', width: '320px' }}>Reporting Manager / Supervisor</th>
              <th style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', textIndent: '10px' }}>{isAdmin ? 'Action' : 'Status'}</th>
            </tr>
          </thead>
          <tbody>
            {reduxLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-5">
                  <Spinner animation="border" variant="primary" size="sm" />
                  <span className="ms-2 text-muted">Loading staff list...</span>
                </td>
              </tr>
            ) : filteredStaffList.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4 text-muted">
                  No staff members found matching your search.
                </td>
              </tr>
            ) : (
              filteredStaffList.map((u) => {
                const uObj = u?.userInfo || u || {};
                const uId = String(uObj._id || uObj.id || u?._id || '');
                const uName = uObj.name || u.name || 'Staff Member';
                const uMobile = uObj.mobileNumber || u.mobileNumber || uObj.mobile || '—';
                const empId = uObj.employee_id || u.employee_id || uObj.employeeId || '';
                const roles = uObj.userRoles || u.userRoles || [];
                const currentManagerId = managerAssignments[uId] || '';
                const isSaving = savingUserId === uId;

                const selectedOption = managerOptions.find(opt => String(opt.value) === String(currentManagerId)) || null;

                return (
                  <tr key={uId} style={{ verticalAlign: 'middle' }}>
                    {/* Employee Name */}
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: '#e2e8f0',
                            color: '#334155',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '13px'
                          }}
                        >
                          {uName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <strong style={{ fontSize: '13.5px', color: '#1e293b', display: 'block' }}>
                            {uName}
                          </strong>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>
                            {uMobile}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Employee ID */}
                    <td>
                      {empId ? (
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            color: '#2563eb',
                            background: '#eff6ff',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            border: '1px solid #bfdbfe'
                          }}
                        >
                          {empId}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                      )}
                    </td>

                    {/* Roles */}
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {roles.length > 0 ? (
                          roles.map(r => (
                            <Badge
                              key={r}
                              bg="light"
                              text="dark"
                              style={{ border: '1px solid #cbd5e1', fontSize: '10px', fontWeight: 600 }}
                            >
                              {r.replace(/_/g, ' ')}
                            </Badge>
                          ))
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '11px' }}>No Role</span>
                        )}
                      </div>
                    </td>

                    {/* Assigned Project */}
                    <td>
                      <span style={{ fontSize: '12px', color: '#334155', fontWeight: 500 }}>
                        {u.assignedProjectName || u.projectName || '—'}
                      </span>
                    </td>

                    {/* Reporting Manager Select Dropdown */}
                    <td>
                      <Select
                        options={managerOptions.filter(opt => opt.value !== uId)}
                        value={selectedOption}
                        isDisabled={!isAdmin}
                        onChange={(opt) => {
                          setManagerAssignments(prev => ({
                            ...prev,
                            [uId]: opt ? opt.value : ''
                          }));
                        }}
                        placeholder="Select Manager..."
                        isClearable={isAdmin}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: base => ({
                            ...base,
                            minHeight: '36px',
                            fontSize: '12.5px',
                            borderRadius: '6px',
                            borderColor: currentManagerId ? '#bfdbfe' : '#cbd5e1',
                            background: !isAdmin ? '#f8fafc' : '#fff'
                          })
                        }}
                      />
                    </td>

                    {/* Action Button */}
                    <td>
                      {isAdmin ? (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={isSaving}
                          onClick={() => handleSaveMapping(u)}
                          style={{
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            padding: '5px 14px',
                            background: '#0284c7',
                            border: 'none'
                          }}
                        >
                          {isSaving ? (
                            <>
                              <Spinner animation="border" size="sm" />
                              <span className="ms-1">Saving...</span>
                            </>
                          ) : (
                            'Save Mapping'
                          )}
                        </Button>
                      ) : (
                        <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic', paddingLeft: '8px' }}>
                          View only
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default ReportingMappingTab;
